import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src={LOGO_ICON_BLUE} alt="Rebuilt" className="h-8 w-8" />
          <h1 className="text-xl font-bold text-gray-900">Terms of Service</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="h-8 w-8 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">Terms of Service</h2>
          </div>

          <p className="text-sm text-gray-500 mb-8">
            <strong>Last Updated:</strong> January 1, 2025
          </p>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h3>
              <p className="text-gray-700 leading-relaxed">
                Welcome to Rebuilt ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of Rebuilt's EMI assistance platform, including our website, mobile application, and related services (collectively, the "Services").
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use our Services.
              </p>
            </section>

            {/* Eligibility */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">2. Eligibility</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                To use our Services, you must:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Be at least 18 years of age</li>
                <li>Be a resident of India</li>
                <li>Have the legal capacity to enter into a binding contract</li>
                <li>Provide accurate and complete registration information</li>
                <li>Not be prohibited from using our Services under applicable laws</li>
              </ul>
            </section>

            {/* Account Registration */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">3. Account Registration and Security</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>3.1 Account Creation:</strong> You must create an account to access certain features of our Services. You agree to provide accurate, current, and complete information during registration.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>3.2 Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must immediately notify us of any unauthorized use of your account.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>3.3 One Account Per User:</strong> Each user may maintain only one active account. Multiple accounts created by the same individual may be terminated without notice.
              </p>
            </section>

            {/* Services Description */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Description of Services</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Rebuilt provides EMI (Equated Monthly Installment) assistance services that enable eligible customers to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Apply for short-term financial assistance for EMI payments</li>
                <li>Choose flexible repayment terms (1, 2, or 3 months)</li>
                <li>Complete KYC verification digitally</li>
                <li>Track application status and manage disbursements</li>
                <li>Make repayments through integrated payment gateways</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                We act as an intermediary platform connecting customers with lending partners. Final approval and disbursement decisions are made by our lending partners based on their underwriting criteria.
              </p>
            </section>

            {/* Application Process */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">5. Application Process and Approval</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>5.1 Application Submission:</strong> By submitting an application, you authorize us and our lending partners to verify your information, conduct credit checks, and evaluate your creditworthiness.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>5.2 No Guarantee of Approval:</strong> Submission of an application does not guarantee approval or disbursement. We reserve the right to reject any application without providing reasons.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>5.3 KYC Verification:</strong> You must complete KYC verification including PAN card and Aadhaar verification as required by applicable regulations. Providing false or misleading information may result in immediate account termination and legal action.
              </p>
            </section>

            {/* Fees and Charges */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Fees and Charges</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>6.1 Interest Rates:</strong> Interest rates are determined based on your credit profile and loan tenure. The applicable interest rate will be clearly disclosed before you accept the loan offer.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>6.2 Processing Fees:</strong> A processing fee may be charged for each approved application. This fee will be clearly disclosed during the application process.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>6.3 Late Payment Charges:</strong> Late payment charges will apply if you fail to make payments by the due date. The applicable late payment fee will be specified in your loan agreement.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>6.4 Payment Gateway Charges:</strong> Payment gateway charges, if any, will be borne by the customer unless otherwise specified.
              </p>
            </section>

            {/* Repayment Obligations */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Repayment Obligations</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>7.1 Timely Repayment:</strong> You agree to repay the loan amount along with applicable interest and fees according to the agreed repayment schedule.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>7.2 Auto-Debit Authorization:</strong> By accepting a loan, you authorize us to debit your registered bank account or charge your registered payment method on the due dates.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>7.3 Default Consequences:</strong> Failure to repay on time may result in:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Additional late payment charges</li>
                <li>Negative impact on your credit score</li>
                <li>Suspension or termination of your account</li>
                <li>Debt recovery proceedings</li>
                <li>Legal action as permitted by law</li>
              </ul>
            </section>

            {/* User Conduct */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">8. User Conduct and Prohibited Activities</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                You agree not to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Provide false, inaccurate, or misleading information</li>
                <li>Use the Services for any illegal or fraudulent purpose</li>
                <li>Attempt to circumvent security measures or access unauthorized areas</li>
                <li>Create multiple accounts to obtain multiple loans simultaneously</li>
                <li>Use automated systems or bots to access the Services</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Services</li>
                <li>Interfere with or disrupt the Services or servers</li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">9. Intellectual Property Rights</h3>
              <p className="text-gray-700 leading-relaxed">
                All content, features, and functionality of the Services, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are the exclusive property of Rebuilt and are protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            {/* Privacy */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">10. Privacy and Data Protection</h3>
              <p className="text-gray-700 leading-relaxed">
                Your privacy is important to us. Our collection, use, and disclosure of personal information is governed by our Privacy Policy. By using our Services, you consent to the collection and use of your information as described in the Privacy Policy.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">11. Third-Party Services and Links</h3>
              <p className="text-gray-700 leading-relaxed">
                Our Services may contain links to third-party websites or integrate with third-party services (e.g., payment gateways, KYC providers). We are not responsible for the content, accuracy, or practices of these third-party services. Your use of third-party services is at your own risk and subject to their respective terms and conditions.
              </p>
            </section>

            {/* Disclaimers */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">12. Disclaimers and Limitations of Liability</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>12.1 "As Is" Basis:</strong> The Services are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied.
              </p>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>12.2 No Warranty:</strong> We do not warrant that the Services will be uninterrupted, error-free, or free from viruses or other harmful components.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>12.3 Limitation of Liability:</strong> To the maximum extent permitted by law, Rebuilt shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of or inability to use the Services.
              </p>
            </section>

            {/* Indemnification */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">13. Indemnification</h3>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Rebuilt, its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from your use of the Services, violation of these Terms, or infringement of any third-party rights.
              </p>
            </section>

            {/* Termination */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">14. Termination</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>14.1 By You:</strong> You may terminate your account at any time by contacting our support team. You remain liable for all outstanding obligations.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>14.2 By Us:</strong> We may suspend or terminate your account immediately, without notice, for any reason, including violation of these Terms or suspected fraudulent activity.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">15. Governing Law and Dispute Resolution</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>15.1 Governing Law:</strong> These Terms shall be governed by and construed in accordance with the laws of India.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>15.2 Jurisdiction:</strong> Any disputes arising from these Terms or your use of the Services shall be subject to the exclusive jurisdiction of the courts in [Your City], India.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">16. Modifications to Terms</h3>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our platform and updating the "Last Updated" date. Your continued use of the Services after such modifications constitutes your acceptance of the revised Terms.
              </p>
            </section>

            {/* Severability */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">17. Severability</h3>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">18. Contact Information</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-800"><strong>Email:</strong> legal@rebuilt.in</p>
                <p className="text-gray-800"><strong>Phone:</strong> +91-XXXX-XXXXXX</p>
                <p className="text-gray-800"><strong>Address:</strong> [Your registered business address]</p>
              </div>
            </section>
          </div>

          {/* Back to Home Button */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
